import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import { Save as SaveIcon, Person as PersonIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { PageTitle } from '../../../shared/components/PageTitle';
import { SettingsCard } from '../../../shared/components/SettingsCard';

export const Profile: React.FC = () => {
    const { user, updateProfile, syncProfile } = useAuth();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                await syncProfile();
                setIsFetching(false);
            } catch (err) {
                setError('Failed to load profile');
                setIsFetching(false);
            }
        };
        fetchProfile();
    }, [syncProfile]);

    useEffect(() => {
        if (user) {
            setName(user.name || '');
            setEmail(user.email || '');
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            await updateProfile({ name, email });
            setSuccess('Profile updated successfully');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setError(null);
        setSuccess(null);
    };

    const fieldStyles = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            height: '54px',
            border: '1px solid #e2e8f0',
            '& fieldset': {
                borderColor: 'transparent',
            },
            '&:hover fieldset': {
                borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                border: '1px solid #6366f1'
            },
        },
        '& .MuiInputBase-input': {
            color: '#1f2937',
            padding: '12px 14px',
            fontSize: '15px'
        }
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
        );
    }

    return (
        <div className="add-edit-admin-container bg-[#F0F4F8] min-h-[calc(100vh-64px)] py-10 px-4">
            <div className="max-w-5xl mx-auto">
                <PageTitle title="Account Profile" />

                <SettingsCard
                    title="Personal Information"
                    subtitle="Manage your public profile information and verify your identity."
                    icon={<PersonIcon sx={{ fontSize: 36, color: '#6366f1' }} />}
                >
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">Full Name</label>
                                <TextField
                                    fullWidth
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter your name"
                                    sx={fieldStyles}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <label className="block text-sm font-bold text-[#1f2937] mb-2.5 ml-1">Email Address</label>
                                <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    disabled
                                    sx={{
                                        ...fieldStyles,
                                        '& .MuiOutlinedInput-root': {
                                            ...fieldStyles['& .MuiOutlinedInput-root'],
                                            backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                            cursor: 'not-allowed'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <div className="mt-12 flex justify-end">
                            <AppButton
                                type="submit"
                                variant="primary"
                                startIcon={<SaveIcon />}
                                loading={loading}
                                sx={{
                                    py: 1,
                                    px: 4,
                                    borderRadius: '10px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backgroundColor: '#6366f1',
                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                                    '&:hover': {
                                        backgroundColor: '#4f46e5',
                                    }
                                }}
                            >
                                Save Profile
                            </AppButton>
                        </div>
                    </form>
                </SettingsCard>
            </div>

            <Snackbar
                open={!!error || !!success}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {error ? (
                    <Alert onClose={handleCloseSnackbar} severity="error" variant="filled" sx={{ width: '100%', borderRadius: '12px' }}>
                        {error}
                    </Alert>
                ) : (
                    <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{ width: '100%', borderRadius: '12px' }}>
                        {success}
                    </Alert>
                )}
            </Snackbar>
        </div>
    );
};
